import { useDraggable } from '@dnd-kit/core';
import { numberToPastelRainbowColor } from '@/javascript/color-helper';
import { Section } from '@/javascript/music-objects.ts';
import { Card } from '@mantine/core';
import React, { useCallback, useMemo, useState } from 'react';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import MidiBlocks from '../midi-blocks';

export interface LibraryPartSectionRenderProps {
	index: number;
	section: Section;
	selected: boolean;
	dragging: boolean;
	measureWidth: number;
	selectSectionId: (id: string) => void;
	deselectSectionId: (id: string) => void;
	leftOffset: number;
}

export const LibraryPartSection: React.FC<LibraryPartSectionRenderProps> = (props) => {
	const {
		index,
		section,
		selected,
		measureWidth,
		selectSectionId,
		deselectSectionId,
		leftOffset,
		dragging,
	} = props;

	const [hovering, setHovering] = useState(false);
	const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
		id: `library-section-${section.id}`,
		data: {
			type: 'library-section',
			sectionId: section.id,
		},
	});
	const deviceLayout = useDeviceLayout();
	const isTouchLayout = deviceLayout.isPhoneOrSmaller || deviceLayout.isTabletOrSmaller;

	const handleClick = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation();
			selected ? deselectSectionId(section.id) : selectSectionId(section.id);
		},
		[selected, selectSectionId, deselectSectionId, section.id]
	);

	const color = useMemo(
		() => numberToPastelRainbowColor(index),
		[index]
	);
	const unselectedColorModifier = dragging ? "1" : hovering ? "3" : "2";
	const selectedColorModifier = dragging ? "5" : "4";
	const scaleTransform = selected ? dragging ? 'scale(1.05)' : hovering ? 'scale(1)' : 'scale(0.98)' : dragging ? 'scale(0.92)' : hovering ? 'scale(0.97)' : 'scale(0.95)';
	const translateTransform = transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : null;
	const composedTransform = [translateTransform, scaleTransform].filter(Boolean).join(' ');

	return (
		section.isEmpty() ?
			null
			:
			<Card
				data-id={section.id}
				style={{
					position: 'absolute',
					outline: "1px solid black",
					height: "100%",
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					left: leftOffset,
					width: section.getLength() * measureWidth,
					cursor: dragging || isDragging ? 'grabbing' : 'grab',
					padding: 0,
					zIndex: selected ? 4 : 3,
					transform: composedTransform,
					opacity: dragging && selected ? 0 : 1,
					visibility: dragging && selected ? 'hidden' : 'visible',
					transition: 'transform 0.3s ease, opacity 0.15s ease',
					touchAction: isTouchLayout ? 'none' : 'auto',
				}}
				bg={selected ? `darkGreen.${selectedColorModifier}` : `${color}.${unselectedColorModifier}`}
				shadow={selected ? "lg" : ""}
				onClick={handleClick}
				onMouseEnter={() => setHovering(true)}
				onMouseLeave={() => setHovering(false)}
				ref={setNodeRef}
				{...listeners}
				{...attributes}
			>
				<MidiBlocks
					section={section}
				/>
			</Card>
	);
};
